import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ThemeToggle } from './components/ThemeToggle'
import { ProtectedRoute } from './components/ProtectedRoute'
import { PlausibleAnalytics } from './components/PlausibleAnalytics'
import { Toaster } from './components/ui/toaster'
import { BackofficeLayout } from './components/backoffice/BackofficeLayout'
import { OnboardingGuard } from './components/OnboardingGuard'
import { CookieConsent } from './components/CookieConsent'
import CompanyOnboarding from './pages/onboarding/CompanyOnboarding'
import { Hero } from './components/Hero'
import { UseCasesSection } from './components/UseCasesSection'
import { AboutSection } from './components/AboutSection'
// ServicesSection removed - consolidated into BunkerSection
import { BunkerSection } from './components/BunkerSection'
import { TeamSection } from './components/TeamSection'
import { NewsSection } from './components/NewsSection'
import ChatWidget from './components/chatbot/ChatWidget'
import { NewsPage } from './pages/NewsPage'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { Dashboard } from './pages/Dashboard'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AcceptInvite from './pages/AcceptInvite'
import UserManagement from './pages/UserManagement'
import { ContentManagement } from './pages/ContentManagement'
import { ContentLibraryPage } from './pages/ContentLibrary'
import { Presentations } from './pages/Presentations'
import CompanyProfile from './pages/CompanyProfile'
import CompaniesList from './pages/admin/CompaniesList'
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard'
import CreateCompany from './pages/CreateCompany'
import EditCompany from './pages/EditCompany'
import CompanyDetailPage from './pages/CompanyDetailPage'
import { Imprint } from './pages/Imprint'
import { PrivacyPolicy } from './pages/PrivacyPolicy'
import { TermsOfService } from './pages/TermsOfService'
import PresentationViewer from './pages/PresentationViewer'
import PresentationBuilder from './pages/PresentationBuilder'
import TeamMemberDetail from './pages/public/TeamMemberDetail'
import BunkerDetail from './pages/public/BunkerDetail'
import ProductDemoPage from './pages/public/ProductDemoPage'

import StyleguidePage from './pages/StyleguidePage'
import { SharedPresentation } from './pages/public/SharedPresentation'
import { Chat } from './pages/Chat'
import { Settings } from './pages/Settings'
import { AccessDenied } from './pages/AccessDenied'
import PresentationAccessManagement from './pages/PresentationAccessManagement'
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './lib/i18n'

// Wrapper component for public pages (no longer forces dark mode - respects user preference)
function PublicPageWrapper({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

// Language Redirect Handler - redirects root to /lang or detected language
function LanguageRedirect() {
  const { i18n } = useTranslation()

  // Check localStorage for saved language preference
  const getSavedLanguage = (): string => {
    const saved = localStorage.getItem('i18nextLng')
    if (saved && SUPPORTED_LANGUAGES.includes(saved as any)) {
      return saved
    }
    // Always default to German if no valid saved preference
    return DEFAULT_LANGUAGE
  }

  const savedLang = getSavedLanguage()

  // Update i18n to use the determined language
  useEffect(() => {
    if (savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang)
      localStorage.setItem('i18nextLng', savedLang)
    }
  }, [savedLang, i18n])

  return <Navigate to={`/${savedLang}`} replace />
}

// Language Route Wrapper - handles language prefix and validates language codes
function LanguageRouteWrapper({ children }: { children: React.ReactNode }) {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()
  const [isLanguageReady, setIsLanguageReady] = useState(false)

  useEffect(() => {
    // Validate language code first
    if (!lang || !SUPPORTED_LANGUAGES.includes(lang as any)) {
      return
    }

    // Sync localStorage with URL language
    localStorage.setItem('i18nextLng', lang)

    // Update i18next language when URL language changes
    if (lang !== i18n.language) {
      // Change language - this should be fast since resources are already loaded
      i18n.changeLanguage(lang).then(() => {
        setIsLanguageReady(true)
      }).catch((err) => {
        console.error('Failed to change language:', err)
        // Still render even if changeLanguage fails
        setIsLanguageReady(true)
      })
    } else {
      setIsLanguageReady(true)
    }
  }, [lang, i18n])

  // Validate language code
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang as any)) {
    return <Navigate to={`/${DEFAULT_LANGUAGE}`} replace />
  }

  // Don't render until language is properly set
  if (!isLanguageReady) {
    return null // or a loading spinner
  }

  return <>{children}</>
}

// Deep-link to the ARIA section of the homepage. Supports both /aria/:lang
// (e.g. /aria/en) and /:lang/aria (e.g. /en/aria) — both land on the home
// route with the #aria hash, which BunkerDetail scrolls to.
function AriaDeepLink() {
  const { lang } = useParams<{ lang?: string }>()
  const target = lang && SUPPORTED_LANGUAGES.includes(lang as any) ? lang : DEFAULT_LANGUAGE
  return <Navigate to={`/${target}#aria`} replace />
}

function Homepage() {
  // Handle hash navigation for initial page load and hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '')
      if (hash) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById(hash)
          if (element) {
            const headerHeight = 80 // Approximate header height
            const elementPosition = element.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - headerHeight

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            })
          }
        }, 100)
      }
    }

    // Handle initial hash
    handleHashChange()

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  return (
    <PublicPageWrapper>
      <Header />
      <Hero />
      <UseCasesSection />
      <AboutSection />
      <BunkerSection />
      <TeamSection />
      <NewsSection />
      <Footer />
      <ChatWidget />
    </PublicPageWrapper>
  )
}

function App() {
  const handleCookieConsent = (preferences: Record<'essential' | 'analytics' | 'marketing', boolean>) => {
    // Enable/disable analytics based on consent
    if (preferences.analytics) {
      // Initialize Plausible Analytics if consented
      console.log('Analytics consented')
    } else {
      console.log('Analytics not consented')
    }
  }

  return (
    <Router>
      <PlausibleAnalytics />
      <Toaster />
      <CookieConsent onConsent={handleCookieConsent} />
      <div className="min-h-screen bg-background text-foreground transition-colors">
        {/* App Routes */}
        <Routes>
          {/* Root redirect - redirect to saved language or default */}
          <Route path="/" element={<LanguageRedirect />} />

          {/* Language-prefixed routes - bunker.ai is the main landing page */}
          <Route path="/:lang" element={<LanguageRouteWrapper><BunkerDetail /><ChatWidget /></LanguageRouteWrapper>} />

          {/* ARIA section deep-links — /aria/:lang and /:lang/aria scroll to #aria on the homepage */}
          <Route path="/aria/:lang" element={<AriaDeepLink />} />
          <Route path="/:lang/aria" element={<AriaDeepLink />} />

          {/* Original homepage (hidden but preserved) */}
          <Route path="/:lang/home" element={<LanguageRouteWrapper><Homepage /></LanguageRouteWrapper>} />

          {/* News page route - public with language prefix */}
          <Route path="/:lang/news" element={<LanguageRouteWrapper><NewsPage /><ChatWidget /></LanguageRouteWrapper>} />

          {/* Login route */}
          <Route
            path="/login"
            element={
              <>
                <div className="fixed top-4 right-4 z-50">
                  <ThemeToggle />
                </div>
                <Login />
              </>
            }
          />

          {/* Register route */}
          <Route
            path="/register"
            element={
              <>
                <div className="fixed top-4 right-4 z-50">
                  <ThemeToggle />
                </div>
                <Register />
              </>
            }
          />

          {/* Forgot password route */}
          <Route
            path="/forgot-password"
            element={
              <>
                <div className="fixed top-4 right-4 z-50">
                  <ThemeToggle />
                </div>
                <ForgotPassword />
              </>
            }
          />

          {/* Reset password route */}
          <Route
            path="/reset-password"
            element={
              <>
                <div className="fixed top-4 right-4 z-50">
                  <ThemeToggle />
                </div>
                <ResetPassword />
              </>
            }
          />

          {/* Accept invitation route */}
          <Route
            path="/accept-invite"
            element={
              <>
                <div className="fixed top-4 right-4 z-50">
                  <ThemeToggle />
                </div>
                <AcceptInvite />
              </>
            }
          />

          {/* Onboarding route - protected */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <div className="fixed top-4 right-4 z-50">
                  <ThemeToggle />
                </div>
                <CompanyOnboarding />
              </ProtectedRoute>
            }
          />

          {/* Dashboard route - protected */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <Dashboard />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* User management route - protected for company admins */}
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <UserManagement />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Settings route - protected */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <Settings />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Company profile route - protected for company admins */}
          <Route
            path="/company/profile"
            element={
              <ProtectedRoute requiredRole="company_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <CompanyProfile />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Company management route - protected for super admins */}
          <Route
            path="/companies"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <CompaniesList />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Admin companies list route (alias) - protected for super admins */}
          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <CompaniesList />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Admin company detail route (alias) - protected for super admins */}
          <Route
            path="/admin/companies/:id"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <CompanyDetailPage />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Admin analytics dashboard route - protected for super admins */}
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <AnalyticsDashboard />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Presentation access management route - protected for super admins */}
          <Route
            path="/presentations/access-management"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <PresentationAccessManagement />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Create company route - protected for super admins */}
          <Route
            path="/companies/create"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <CreateCompany />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Company detail route - protected for super admins */}
          <Route
            path="/companies/:id"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <CompanyDetailPage />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Edit company route - protected for super admins */}
          <Route
            path="/companies/:id/edit"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <OnboardingGuard>
                  <BackofficeLayout>
                    <EditCompany />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Content management route - protected for company admins */}
          <Route
            path="/content"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <ContentManagement />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Content library route - protected for all authenticated users */}
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <ContentLibraryPage />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Presentations library route - protected for all authenticated users */}
          <Route
            path="/presentations"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <Presentations />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Presentation builder route - protected for all authenticated users */}
          <Route
            path="/presentations/new"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <PresentationBuilder />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Presentation edit route - protected for all authenticated users */}
          <Route
            path="/presentations/:id/edit"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <PresentationBuilder />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Presentation viewer route - protected for all authenticated users */}
          {/* Note: Viewer renders in full-screen mode without BackofficeLayout to prevent scrollbars */}
          <Route
            path="/presentations/:id"
            element={
              <ProtectedRoute>
                <PresentationViewer />
              </ProtectedRoute>
            }
          />

          {/* Legacy presentation viewer route for backwards compatibility */}
          <Route
            path="/presentations/viewer/:id"
            element={
              <ProtectedRoute>
                <PresentationViewer />
              </ProtectedRoute>
            }
          />

          {/* Fallback presentation route for direct links */}
          <Route
            path="/presentation/:id"
            element={
              <ProtectedRoute>
                <Navigate to="/presentations/:id" replace />
              </ProtectedRoute>
            }
          />

          {/* Chat route - protected for all authenticated users */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <BackofficeLayout>
                    <Chat />
                  </BackofficeLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            }
          />

          {/* Admin panel route - redirect to companies for super admins */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <Navigate to="/companies" replace />
              </ProtectedRoute>
            }
          />

          {/* Access denied page */}
          <Route path="/access-denied" element={<AccessDenied />} />

          {/* Imprint page */}
          <Route path="/:lang/imprint" element={<LanguageRouteWrapper><Imprint /><ChatWidget /></LanguageRouteWrapper>} />

          {/* Privacy Policy page */}
          <Route path="/:lang/privacy" element={<LanguageRouteWrapper><PrivacyPolicy /><ChatWidget /></LanguageRouteWrapper>} />

          {/* Terms of Service page */}
          <Route path="/:lang/terms" element={<LanguageRouteWrapper><TermsOfService /><ChatWidget /></LanguageRouteWrapper>} />

          {/* Team member detail page */}
          <Route path="/:lang/team/:memberId" element={<LanguageRouteWrapper><TeamMemberDetail /><ChatWidget /></LanguageRouteWrapper>} />

          {/* Bunker detail page - redirect to main landing page */}
          <Route path="/:lang/bunker" element={<LanguageRouteWrapper><BunkerDetail /><ChatWidget /></LanguageRouteWrapper>} />

          {/* Full-page interactive product demo */}
          <Route path="/:lang/demo" element={<LanguageRouteWrapper><ProductDemoPage /></LanguageRouteWrapper>} />

          {/* Styleguide / design-system preview (E24-S3) */}
          <Route path="/:lang/styleguide" element={<LanguageRouteWrapper><StyleguidePage /></LanguageRouteWrapper>} />

          {/* Shared presentation page (public, no authentication required) */}
          <Route path="/shared/:token" element={<SharedPresentation />} />

          {/* Catch all - redirect to home with default language */}
          <Route path="*" element={<Navigate to={`/${DEFAULT_LANGUAGE}`} replace />} />
        </Routes>
      </div>
    </Router>
  )
}

export default App
